import React, { useState, useEffect } from 'react';
import { callNativeMethod } from 'src/mobile-ecomm/hooks/use-mobile-native-bridge';
import {
  useGetCustomerCommunicationSettingsQuery,
  useGetDutchiePosLoyaltyConfigQuery,
  useUpdateCustomerCommunicationSettingsMutation,
} from 'types/graphql';

import styled from 'styled-components';
import { useObserver } from 'mobx-react-lite';

import _ from 'lodash';

import useTranslation from 'hooks/use-translation';
import { useUpdateUser } from 'src/state/actions/users';
import { DesktopOnly, SmallLoader } from 'shared/components';
import useErnie from 'shared/hooks/use-ernie';

import { ERNIE_TYPES } from 'shared/constants';
import { Typography } from 'src/typography';
import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { detectLoyaltyIntegration, Integrations } from 'src/components/loyalty/use-loyalty';
import NotificationSettings from './notification-settings';
import SubscriptionList from './subscription-list';
import { TabHeaderTitle, StyledButton } from '../components';
import PushNotificationSettings from './push-notification-settings';
import CommunicationSettings from './communication-settings';

const Preferences = ({ user, subscriptions = [], unsubscribeToDispensaries = null }) => {
  const { t } = useTranslation();
  const updateUser = useUpdateUser();
  const showErnie = useErnie();
  const { dispensary } = useDispensary();
  const emailNotifications = useObserver(() => user.profile.emailNotifications);
  const textNotifications = useObserver(() => user.profile.textNotifications);
  const hasSubscriptions =
    !_.isEmpty(subscriptions) && _.some(subscriptions, (item) => item.textSubscription || item.emailSubscription);

  const [isSaving, setIsSaving] = useState(false);
  const [subscriptionsToCancel, setsubscriptionsToCancel] = useState([]);
  const [checkEmailNotification, setCheckEmailNotification] = useState(false);
  const [checkTextNotification, setCheckTextNotification] = useState(false);

  const loyaltyProgram = detectLoyaltyIntegration(dispensary);
  const skipCommunicationSettingsQuery = loyaltyProgram !== Integrations.dutchiepos || !dispensary?.id || !user?.id;

  const { data: loyaltyConfigData, loading: loyaltyConfigLoading } = useGetDutchiePosLoyaltyConfigQuery({
    variables: {
      dispensaryId: dispensary?.id,
    },
    skip: skipCommunicationSettingsQuery,
  });

  const shouldShowCommunicationSettings =
    loyaltyProgram === Integrations.dutchiepos && loyaltyConfigData?.getDutchiePosLoyaltyConfig?.isEnabled;

  const {
    data: communicationSettings,
    loading: communicationSettingsLoading,
    refetch: refetchCommunicationSettings,
  } = useGetCustomerCommunicationSettingsQuery({
    variables: {
      dispensaryId: dispensary?.id,
      userId: user?.id,
    },
    skip: skipCommunicationSettingsQuery,
  });
  const { optInEmail, optInSMS, optInPush } = communicationSettings?.getCustomerCommunicationSettings ?? {};
  const [optInState, setOptInState] = useState({
    email: optInEmail,
    sms: optInSMS,
    push: optInPush,
  });

  const handleOptInClick = (type) => {
    setOptInState((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const [updateCustomerCommunicationSettingsMutation] = useUpdateCustomerCommunicationSettingsMutation();

  const { isMobileEcommApp } = useUI();

  useEffect(() => {
    setCheckEmailNotification(emailNotifications);
  }, [emailNotifications]);

  useEffect(() => {
    setCheckTextNotification(textNotifications);
  }, [textNotifications]);

  useEffect(() => {
    setOptInState((prev) => ({
      email: optInEmail !== undefined ? optInEmail : prev.email,
      sms: optInSMS !== undefined ? optInSMS : prev.sms,
      push: optInPush !== undefined ? optInPush : prev.push,
    }));
  }, [optInEmail, optInSMS, optInPush]);

  useEffect(() => {
    if (isMobileEcommApp) {
      callNativeMethod(`onSetNotificationStatus`);
    }
  }, [isMobileEcommApp]);

  const handleToggleSubscribe = (subscriptionId) => {
    if (_.includes(subscriptionsToCancel, subscriptionId)) {
      const newSubs = _.filter(subscriptionsToCancel, (id) => id !== subscriptionId);

      setsubscriptionsToCancel(newSubs);
    } else {
      setsubscriptionsToCancel([...subscriptionsToCancel, subscriptionId]);
    }
  };

  async function handleCommunicationSettingsSave() {
    try {
      setIsSaving(true);
      await updateCustomerCommunicationSettingsMutation({
        variables: {
          dispensaryId: dispensary.id,
          userId: user.id,
          optInEmail: optInState.email,
          optInSMS: optInState.sms,
          optInPush: optInState.push,
        },
      });
      await refetchCommunicationSettings();
      showErnie('Communication settings updated successfully.', 'success');
    } catch {
      showErnie('Error updating communication settings. Please try again.', 'danger');
    }
  }

  async function handleSave() {
    const { email, profile } = user;
    const notifications = {
      emailNotifications: checkEmailNotification,
      textNotifications: checkTextNotification,
    };

    setIsSaving(true);

    try {
      await updateUser({
        email,
        profile: { ...profile, ...notifications },
      });
      user.refetch();
      if (subscriptionsToCancel && unsubscribeToDispensaries) {
        await unsubscribeToDispensaries(subscriptionsToCancel);
      }
      if (shouldShowCommunicationSettings) {
        await handleCommunicationSettingsSave();
      }
      showErnie(t('preferences.updateSuccess', 'Preferences successfully updated!'), ERNIE_TYPES.SUCCESS);
      setIsSaving(false);
    } catch (err) {
      console.log(err);
      showErnie(
        t('preferences.unableToUpdate', 'We were unable to save your information. Please try again later.'),
        ERNIE_TYPES.DANGER
      );
      setIsSaving(false);
    }
  }

  function handleCheckboxClick({ name }) {
    if (name === 'textNotifications') {
      setCheckTextNotification(!checkTextNotification);
    } else {
      setCheckEmailNotification(!checkEmailNotification);
    }
  }

  const isLoading = communicationSettingsLoading || isSaving || loyaltyConfigLoading;

  return (
    <PreferencesContainer $isLoading={isLoading}>
      {isLoading && (
        <LoadingOverlay>
          <SmallLoader />
        </LoadingOverlay>
      )}
      <DesktopOnly>
        <TabHeaderTitle tag='h2' size='medium'>
          {(props) => props.t('accountsPage.preferences', 'Preferences')}
        </TabHeaderTitle>
      </DesktopOnly>

      <SubHeader tag='h3' size='xsmall'>
        {(props) => props.t('accountsPage.notifications', 'Notification Settings')}
      </SubHeader>

      <NotificationSettings
        onCheckboxClick={handleCheckboxClick}
        checkEmailNotification={checkEmailNotification}
        checkTextNotification={checkTextNotification}
      />

      {hasSubscriptions && (
        <>
          <SubscriptionsSubHeader tag='h3' size='xsmall'>
            {(props) => props.t('accountsPage.subscriptions', 'Subscriptions')}
          </SubscriptionsSubHeader>

          <SubHeaderCopy>
            {t('accountsPage.promotion', 'Receive promotional emails from your favorite dispensaries.')}
          </SubHeaderCopy>

          <StyledSubscriptionList
            subscriptions={subscriptions}
            subscriptionsToCancel={subscriptionsToCancel}
            onToggleSubscribe={handleToggleSubscribe}
          />
        </>
      )}

      {shouldShowCommunicationSettings && (
        <CommunicationSettingsContainer>
          <SubHeader tag='h3' size='xsmall'>
            {(props) => props.t('accountsPage.loyalty', 'Communication Settings')}
          </SubHeader>

          <CommunicationSettings
            optInEmail={optInState.email}
            optInSMS={optInState.sms}
            optInPush={optInState.push}
            onOptInEmailClick={() => handleOptInClick('email')}
            onOptInSMSClick={() => handleOptInClick('sms')}
            onOptInPushClick={() => handleOptInClick('push')}
          />
        </CommunicationSettingsContainer>
      )}

      <PreferencesStyledButton onClick={handleSave} loading={isSaving}>
        {t('common.save', 'Save')}
      </PreferencesStyledButton>
      {isMobileEcommApp && <PushNotificationSettings />}
    </PreferencesContainer>
  );
};

export default Preferences;

const PreferencesContainer = styled.div`
  position: relative;
  opacity: ${({ $isLoading }) => ($isLoading ? 0.5 : 1)};
  pointer-events: ${({ $isLoading }) => ($isLoading ? 'none' : 'auto')};
  transition: opacity 0.2s ease;
`;

const CommunicationSettingsContainer = styled.div`
  margin-top: 21px;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
`;
const SubscriptionsSubHeader = styled(Typography.Heading)`
  margin-top: 61px;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
`;

const SubHeader = styled(Typography.Heading)`
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
`;

const SubHeaderCopy = styled.div`
  margin-top: 9px;
  font-size: 12px;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 13px;
  }
`;

const StyledSubscriptionList = styled(SubscriptionList)`
  margin-bottom: 32px;
`;

const PreferencesStyledButton = styled(StyledButton)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-top: 32px;
  }
`;
